import { useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import CoursePromoCard from '@components/shared/bytes/course-promo-card/CoursePromoCard';

const RelatedContentCard = dynamic(
  () => import('@components/shared/bytes/related-content-card/RelatedContentCard'),
  { ssr: false }
);
const ShareButtons = dynamic(() => import('@components/shared/bytes/share-buttons/ShareButtons'), {
  ssr: false,
});
import BytesFooter from '@components/shared/bytes/bytes-footer/BytesFooter';
import Image from 'next/image';
import cn from 'classnames';
import FileIcon from '@assets/bytes/file-icon.svg';
import Link from 'next/link';

const ArticleCard = dynamic(() => import('@components/shared/bytes/article-card/ArticleCard'), {
  ssr: false,
});

const TutorialCard = dynamic(
  () => import('@components/shared/bytes/bytes-tutorials/TutorialCard'),
  {
    ssr: false,
  }
);

import { getArticles, getBytesStrapiUrl } from '@lib/bytesStrapiServices';
import axios from 'axios';
import ViewsAndReadTime from '@components/shared/bytes/article-card/ViewsAndReadTime';
import SiteHeader from '@components/shared/SiteHeader';
import MarkDownPreview from '@components/reuseable/MarkDownPreview';
import CompilerPromoCard from '@components/shared/bytes/CompilerPromoCard';
import DarkLayout from '@components/shared/DarkLayout';
import { getImgUrl } from '@utils/functions/getImgUrl';
import { Primary, Secondary, SecondaryColor } from 'stories/Button.stories';
import { PrimaryBadge } from 'stories/Badge.stories';
import { LANG_MAP } from '@lib/constants';
import { useStore } from '@globalStore/storeProvider';
import { SlGraduation } from 'react-icons/sl';
import { getLessonsLength } from '@components/shared/bytes/bytes-tutorials/TutorialCard';
import { IoBookOutline } from 'react-icons/io5';
import { AiOutlineUser } from 'react-icons/ai';
import { HiOutlineGlobeAsiaAustralia } from 'react-icons/hi2';
import { RiHandCoinLine } from 'react-icons/ri';
import moment from 'moment';

const Tutorial = ({ singleTutorial }) => {
  const {
    description,
    title,
    tags,
    modules,
    views,
    slug,
    seo,
    description_markdown,
    compiler,
    heroImage,
    lastUpdatedAt,
    updatedAt,
  } = singleTutorial?.data?.attributes || {};
  const { metaTitle, metaDescription, canonicalURL, structuredData, metaImage, metaRobots } =
    seo || {};
  const { slug: firstLesson } =
    modules?.data?.[0]?.attributes?.lessons?.data?.[0]?.attributes || {};

  const sortedModules = useMemo(() => {
    // Sort the array of objects based on their serialNo property
    return modules?.data?.sort((a, b) => a.serialNo - b.serialNo);
  }, [modules?.data]);

  const { relatedArticles, relatedTutorials } = singleTutorial?.meta;

  const startLearningCTALink = `/bytes/tutorials/${slug}/${firstLesson}`;

  const { showStripBanner } = useStore();

  const totalLessons = getLessonsLength(modules?.data);

  const scrollToCurriculum = () => {
    const element = document.getElementById('curriculum');

    window.scrollTo({
      top: element.getBoundingClientRect().top + window.pageYOffset - 170,
    });
  };

  return (
    <>
      <SiteHeader
        image={metaImage?.data?.attributes?.url}
        title={metaTitle}
        desc={metaDescription}
        canonicalUrl={canonicalURL}
        structuredData={structuredData}
        metaRobots={metaRobots}
      />

      <section className="flex flex-col">
        <DarkLayout customStyle="gap-y-[48px] px-[16px] pt-[32px] pb-[46px] md:grid-cols-2 gap-x-[32px] md:px-[124px] md:pb-[83px] md:pt-[40px]">
          <div className="z-1 relative flex flex-col items-start gap-y-[12px] text-white md:gap-y-[16px]">
            {tags?.data?.length > 0 && (
              <div className="flex items-center gap-[8px]">
                {tags.data.slice(0, 2)?.map((item) => (
                  <PrimaryBadge label={item.attributes.name} key={item.id} size={'large'} />
                ))}
              </div>
            )}
            <h1 className="font-satoshi-bold font-satoshi-bold text-[36px] font-bold  leading-10 tracking-wide md:text-[48px] md:leading-tight">
              {title}
            </h1>
            <div className="flex items-center gap-x-[16px] font-satoshi-medium text-[#D0D0D0]">
              {modules?.data?.length && (
                <span className="flex items-center gap-x-[8px]">
                  <SlGraduation size={20} />
                  <span className="text-[14px] leading-5">{`${modules?.data?.length} Modules`}</span>
                </span>
              )}

              <span className="flex items-center gap-x-[8px]">
                <IoBookOutline size={20} />
                <span className="text-[14px] leading-5">{`${totalLessons} Lessons`}</span>
              </span>

              <span className="flex items-center gap-x-[8px]">
                <AiOutlineUser size={20} />
                <span className="text-[14px] leading-5">{`${views} Learners`}</span>
              </span>
            </div>
            <p className="font-satoshi-regular text-[16px] leading-6 md:text-[18px] md:leading-7">
              {description}
            </p>
            <Primary label={'Start Learning'} size={'large'} linkCTA href={startLearningCTALink} />

            <span className="font-satoshi-regular text-[14px] leading-5">
              Last Updated: {moment(lastUpdatedAt || updatedAt).format('Do MMMM, YYYY')}
            </span>
          </div>
          {heroImage?.data && (
            <div>
              <img src={getImgUrl(heroImage)} className="w-full rounded-xl object-contain" />
            </div>
          )}
        </DarkLayout>
        <div className="grid w-full gap-y-[48px] bg-[#FCFCFD] px-[16px] pt-[32px] pb-[71px] md:grid-cols-12 md:gap-x-[32px] md:bg-white md:px-[124px] md:pt-[64px] md:pb-[88px]">
          <div className="flex flex-col gap-y-[32px] md:col-span-8">
            <section className="font-satoshi-regular">
              <MarkDownPreview content={description_markdown} />
            </section>

            <h2 className="font-satoshi-bold text-[24px] leading-8">Course Curriculum</h2>
            <section className="flex flex-col gap-y-[24px]" id="curriculum">
              {sortedModules?.map(({ attributes: { name, lessons } }, index) => (
                <details key={index} open={!index} className="rounded-2xl bg-[#FCFCFD] p-[24px]">
                  <summary className="cursor-pointer outline-none">
                    <h3 className="flex flex-col items-start gap-y-[2px] font-satoshi-medium font-satoshi-medium text-[18px] font-medium  leading-7">
                      <span className="text-[14px] leading-5 text-gray-navigation">
                        Module {index + 1}
                      </span>
                      {name}
                    </h3>
                  </summary>

                  <section className="flex flex-col gap-y-[16px] pt-[16px]">
                    {lessons?.data
                      ?.sort((a, b) => (a.serialNo > b.serialNo ? -1 : 1))
                      ?.map(({ attributes: { title, slug: lessonSlug } }) => (
                        <Link
                          href={`/bytes/tutorials/${slug}/${lessonSlug}`}
                          as={`/bytes/tutorials/${slug}/${lessonSlug}`}
                          passHref
                          key={title}
                        >
                          <a className="flex items-center gap-x-[4px]">
                            <Image
                              src={FileIcon}
                              height={24}
                              width={24}
                              objectFit="contain"
                              alt="file-icon"
                            />
                            <h4 className="font-satoshi-medium font-satoshi-medium text-[14px] font-medium  leading-5 text-[#1A1A1A]">
                              {title}
                            </h4>
                          </a>
                        </Link>
                      ))}
                  </section>
                </details>
              ))}
            </section>
          </div>

          <div
            className={cn(
              'slim-scroll order-first flex flex-col gap-y-[24px] self-start overflow-y-scroll rounded-xl border border-[#F2F4F7] bg-white p-[16px] md:sticky md:order-none md:col-span-4 md:h-[75vh] md:p-[24px]',
              {
                'md:top-14': !showStripBanner,
                'md:top-32': showStripBanner,
              }
            )}
          >
            <div className="hidden w-full md:block">
              <TutorialCTA
                learningLink={startLearningCTALink}
                compilerData={compiler?.data?.attributes}
                exploreCurriculum={scrollToCurriculum}
              />
            </div>

            <hr className="hidden text-[#F2F4F7] md:block" />

            <div className="flex flex-col gap-y-[16px]">
              <h2 className="font-satoshi-bold text-[18px] leading-7 md:text-[20px] md:leading-[30px]">
                Summary
              </h2>

              <div className="flex flex-col gap-y-[24px] font-satoshi-medium">
                <span className="flex items-center gap-x-[8px]">
                  <SlGraduation size={24} className="text-red" />
                  <span className="text-[14px] leading-5 text-[#1A1A1A]">
                    Written by Industry Experts
                  </span>
                </span>

                <span className="flex items-center gap-x-[8px]">
                  <RiHandCoinLine size={24} className="text-red" />
                  <span className="text-[14px] leading-5 text-[#1A1A1A]">
                    Self-paced Learning with Unlimited Access
                  </span>
                </span>

                <span className="flex items-center gap-x-[8px]">
                  <HiOutlineGlobeAsiaAustralia size={24} className="text-red" />
                  <span className="text-[14px] leading-5 text-[#1A1A1A]">
                    Earn a Certificate Upon Compeletition
                  </span>
                </span>
              </div>
            </div>

            <hr className="text-[#F2F4F7]" />

            <ShareButtons grayout />

            <hr className="block text-[#F2F4F7] md:hidden" />

            <div className="block w-full md:hidden">
              <Secondary
                size={'xl'}
                label={'Explore Curriculum'}
                onClick={scrollToCurriculum}
                customStyle="w-full"
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white p-[16px] md:hidden">
          <TutorialCTA
            learningLink={startLearningCTALink}
            compilerData={compiler?.data?.attributes}
            mobile
          />
        </div>

        <div className="flex w-full flex-col gap-y-16 bg-[#FAFAFA] py-12 px-4 md:border-t md:border-[#E0E0E0] md:bg-transparent md:px-[124px] md:py-20">
          {/* <BytesFooter /> */}
          <div className="flex flex-col items-center gap-y-16 md:items-start">
            <p className="font-satoshi-regular font-satoshi-bold text-[30px] font-bold  leading-9">
              Top Tutorials
            </p>
            <div className="grid gap-8 md:grid-cols-3">
              {relatedTutorials?.slice(0, 3).map(({ id, ...attributes }) => (
                <TutorialCard key={id} {...attributes} />
              ))}
            </div>
          </div>

          <div className="flex flex-col items-center gap-y-16 md:items-start">
            <p className="font-satoshi-regular font-satoshi-bold text-[30px] font-bold  leading-9">
              Related Articles
            </p>
            <div className="grid gap-8 md:grid-cols-3">
              {relatedArticles?.slice(0, 3).map(({ id, ...attributes }) => (
                <ArticleCard key={id} {...attributes} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
export default Tutorial;

export async function getStaticProps({ params }) {
  const { slug } = params;
  try {
    const apiBaseUrl = getBytesStrapiUrl();
    const singleTutorial = await axios.get(
      `${apiBaseUrl}/api/tutorials/${slug}?populate=tags,subjects,modules.lessons,seo,seo.metaImage,heroImage,cardImage,views,description_markdown,compiler`
    );

    // const articles = await getArticles({ page: 1, size: 9 });

    if (!singleTutorial?.data?.data) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        // articlesData: articles?.data || {},
        // tutorialAndFilters: tutorialAndFilters?.data || {},
        singleTutorial: singleTutorial?.data || {},
      },
      revalidate: 1800,
    };
  } catch (error) {
    console.log(error);
    return {
      notFound: true,
    };
  }
}

export async function getStaticPaths() {
  // Fetch a list of all possible slugs
  const apiBaseUrl = getBytesStrapiUrl();
  const { data } = await axios.get(
    `${apiBaseUrl}/api/tutorials?fields[0]=slug&pagination[pageSize]=100`
  );

  const paths =
    data.data
      ?.filter(({ attributes }) => attributes.slug)
      ?.map(({ attributes: { slug } }) => ({
        params: { slug },
      })) || [];

  return { paths, fallback: 'blocking' };
}

function TutorialCTA({ learningLink, compilerData, mobile = false }) {
  const { language } = compilerData || {};

  return (
    <div
      className={cn({
        'grid grid-cols-1 grid-rows-2 gap-y-[12px]': !mobile && !!language,
        'grid grid-cols-1 grid-rows-1 gap-y-[12px]': !mobile && !language,
        'grid grid-cols-2 gap-x-[16px]': mobile && !!language,
        grid: !language && mobile,
      })}
    >
      {learningLink && <Primary size={'xl'} label={'Start Learning'} linkCTA href={learningLink} />}

      {language && (
        <SecondaryColor
          size={'xl'}
          label={'Try Compiler'}
          linkCTA
          href={`/online-compiler/${LANG_MAP[language]}`}
        />
      )}
    </div>
  );
}
